import type { Project } from './types';

/**
 * One project, presented as a full-width case study rather than a filterable
 * card grid — a grid with a single card, and filter chips over a single item,
 * looks broken (PLAN.md §4.4). The grid path stays available behind this same
 * shape once a second project exists.
 */
export const projects: Project[] = [
  {
    id: 'devforge-ai',
    title: 'DevForge AI',
    year: '2026',
    subtitle:
      'Distributed LLM inference platform — independently designed, built and deployed as a five-service Docker Compose stack',
    blurb:
      'A Spring Boot control plane orchestrating Python FastAPI workers that run Ollama-hosted models, with load-aware scheduling, heartbeat health monitoring, and automatic requeue of in-flight tasks when a worker dies.',
    tags: ['AI/ML', 'Distributed Systems', 'Backend', 'Full Stack', 'DevOps'],
    tech: [
      'Java',
      'Spring Boot',
      'Python',
      'FastAPI',
      'React',
      'Vite',
      'Ollama',
      'MySQL',
      'Docker',
      'Docker Compose',
      'JaCoCo',
      'pytest-cov',
      'Vitest',
    ],
    repoUrl: 'https://github.com/RSRev27/Dev_Forge_AI',
    featured: true,

    metrics: [
      { label: 'Worker heartbeat', value: '10s' },
      { label: 'Task poll interval', value: '3s' },
      { label: 'Backend sweep', value: '5s' },
      { label: 'Offline threshold', value: '60s' },
      { label: 'Test coverage gate', value: '100%' },
      { label: 'Services in stack', value: '5' },
    ],

    sections: [
      {
        heading: 'Architecture',
        body: 'A Spring Boot control plane orchestrates Python FastAPI workers running Ollama-hosted models. Full conversation history is persisted in MySQL and replayed to the model each turn, so context survives worker churn rather than living in process memory.',
      },
      {
        heading: 'Scheduling',
        inResume: false,
        body: 'Workers register with the control plane and pull tasks. The backend ranks compatible workers by current load, then GPU, VRAM, RAM, and core count, routing each request to the model the user selected from the available Ollama-hosted models.',
      },
      {
        heading: 'Concurrency',
        inResume: false,
        body: 'Scales horizontally across worker containers that register dynamically with the control plane. Per-worker task slots are configurable, so total capacity is containers times slots and can be tuned without redeploying the control plane.',
      },
      {
        heading: 'Fault tolerance',
        body: 'Workers heartbeat every 10 seconds and poll for work every 3. A backend sweep runs every 5 seconds and marks a worker offline after 60 seconds of silence, requeueing its in-flight tasks so a dying worker loses no requests.',
      },
      {
        heading: 'Deployment',
        inResume: false,
        body: 'A five-service Docker Compose stack secured with API-key authentication and hardened, non-root containers.',
      },
      {
        heading: 'Quality gates',
        body: 'Build-failing coverage gates across all three application services, via JaCoCo, pytest-cov, and Vitest.',
      },
    ],
  },
];
